import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  Observable,
  combineLatest,
  concat,
  of,
  retry,
  switchMap,
  shareReplay,
  catchError,
  filter,
  take,
  map,
} from 'rxjs';

import { environment } from '@ers-cat-app/env/environment';
import { User } from '@prisma/client';
import { Role } from '@prisma/client';
import {
  AdManager,
  AdUserWithDepartment,
  AdUserWithManager,
  UserWithRoles,
} from '@ers/shared';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly BASE_URL = `${environment.apiUrl}/user`;

  // Main reactive stream for current user - replaces the old broken user$ getter
  readonly user$: Observable<UserWithRoles | null>;

  constructor() {
    // Create reactive user stream that updates automatically
    this.user$ = combineLatest([
      this.authService.isLoggedIn$,
      this.authService.accessToken$,
    ]).pipe(
      switchMap(([isLoggedIn, accessToken]) => {
        if (
          !isLoggedIn ||
          !this.authService.isAppAccessToken(accessToken)
        ) {
          return of(null);
        }

        const expectedEmail = this.authService.getEmailFromToken(accessToken);
        return concat(
          of(null),
          this.profile(accessToken).pipe(
            map(user =>
              user &&
              expectedEmail &&
              user.email?.toLowerCase() === expectedEmail.toLowerCase()
                ? user
                : null,
            ),
            retry({ count: 2, delay: 3000 }),
            catchError(error => {
              console.error('Error fetching user profile:', error);
              return of(null);
            }),
          ),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** One-shot read after login. Use user$ for screens that stay subscribed. */
  waitForCurrentUser(): Observable<UserWithRoles | null> {
    return this.user$.pipe(
      filter(user => {
        if (!this.authService.isLoggedIn$.value) {
          return true;
        }

        const tokenEmail = this.authService.getEmailFromToken();
        return (
          !!user &&
          !!tokenEmail &&
          user.email?.toLowerCase() === tokenEmail.toLowerCase()
        );
      }),
      take(1),
    );
  }

  list(): Observable<User[]> {
    return this.http.get<User[]>(this.BASE_URL);
  }

  get(id: number): Observable<UserWithRoles> {
    return this.http.get<UserWithRoles>(`${this.BASE_URL}/${id}`);
  }

  update(user: Partial<User>): Observable<Partial<User>> {
    return this.http.patch(`${this.BASE_URL}`, user);
  }

  updateUserWithRoles(
    user: Partial<User>,
    newRoles: Role[],
  ): Observable<Partial<User>> {
    return this.http.post(`${this.BASE_URL}/userWithRoles`, {
      user,
      roles: newRoles,
    });
  }

  delete(id: number): Observable<Partial<User>> {
    return this.http.delete(`${this.BASE_URL}/${id}`);
  }

  profilePicture(): Observable<Blob | null> {
    if (!this.authService.isAzureUser()) {
      return of(null);
    }

    const userId = this.authService.getUserIdFromToken();
    return this.http.get<Blob>(`${this.BASE_URL}/profile/picture`, {
      responseType: 'blob' as 'json',
      headers: {
        'Cache-Control': 'no-store',
        Pragma: 'no-cache',
      },
      params: userId != null ? { userId } : {},
    });
  }

  getProfilePicture(email: string): Observable<Blob | null> {
    return this.http.get<Blob>(`${this.BASE_URL}/profile/picture/${email}`, {
      responseType: 'blob' as 'json',
    });
  }

  getAdUsers(): Observable<AdUserWithDepartment[]> {
    return this.http.get<AdUserWithDepartment[]>(
      `${this.BASE_URL}/ad/safety-report`,
    );
  }

  getAdUsersManager(userId: string): Observable<AdManager> {
    return this.http.get<AdUserWithManager>(
      `${this.BASE_URL}/ad/safety-report/manager/${userId}`,
    );
  }

  private profile(accessToken?: string | null): Observable<UserWithRoles> {
    const userId = this.authService.getUserIdFromToken(accessToken);
    return this.http.get<UserWithRoles>(`${this.BASE_URL}/profile`, {
      headers: {
        'Cache-Control': 'no-store',
        Pragma: 'no-cache',
      },
      params: userId != null ? { userId } : {},
    });
  }
}
